import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CompassLogo } from "./CompassLogo";
import {
  LayoutDashboard, Target, TrendingUp, BarChart3,
  Users, Settings as SettingsIcon, LogOut,
  BookOpen, ChevronLeft, ChevronRight, Info, MapPin, ShieldCheck,
} from "lucide-react";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { NotificationsBell } from "./NotificationsBell";
import { AccountMenu } from "./AccountMenu";
// Issue #780 (D140) — the kit's real navy TopBar shell, reused here (not
// reimplemented) for the one shared-header route that draws it:
// `/coach-portal`'s redrawn empty state. See the `<header>` conditional
// below for why only that route qualifies. The drawn branch itself lives in
// `DrawnTopBar.jsx` (#1121): a coach gets the white bar and the coach ribbon.
import { TopBar } from "./kit";
import DrawnTopBar from "./DrawnTopBar";
import { signOutAndGoHome } from "../lib/signOut";
import { homePathForRole, homeLabelForRole } from "../lib/homePath";
// Issue #730/#754, PR #777 review F4/F5. `CHROMELESS_ROUTES` and
// `isChromelessRoute` live in `lib/chromelessRoutes.js`, a pure-JS module
// with no `react-router-dom` import, specifically so they are directly
// unit-testable — see that file's own docstring for why importing
// `Layout.jsx` itself (which pulls in react-router-dom's ESM-only build)
// breaks Jest's resolver. Re-exported here unchanged so #754's own
// acceptance criterion ("`Layout.jsx` exports `CHROMELESS_ROUTES`") and
// #755's consuming import (`from "../components/Layout"`) both still hold.
import { CHROMELESS_ROUTES, isChromelessRoute, hidesTextSizeControl } from "../lib/chromelessRoutes";
// Issue #804 fix round, F1 — the nav rename is gated on the screen it came
// from, not shipped as a bare copy change. `newUi` is read fresh on every
// call by design (see that file's own docstring), so gating it here at
// render time has no caching problem.
import { newUi } from "../lib/featureFlags";
// Issue #797 fix round, B1 — lets the rendered screen (currently only
// `CoachPortalEmptyState`) declare itself as text-size-retired directly,
// for the one route where route+flag alone can't tell two compositions of
// the same page apart. See that module's own docstring.
import { RetireTextSizeProvider } from "../context/RetireTextSizeContext";
// Issue #757 review round 2, B1 — the sibling mechanism, one layer up: lets
// the rendered screen declare "I supply my own complete page" for a route
// where route+flag isn't the whole truth. See that module's own docstring.
import { ChromelessOverrideProvider } from "../context/ChromelessOverrideContext";
import { PageWidthProvider } from "../context/PageWidthContext";

export { CHROMELESS_ROUTES, isChromelessRoute, getNavForRole, SidebarItem };

const COLLAPSE_KEY = "cc_sidebar_collapsed";

// Fixed, role-keyed nav (spec §2 / issue #7) — no accordion children, 6 items
// per player persona, 4 per coach persona.
//
// Issue #58 (spec §4.3): Grounds is a deliberate, reviewed 7th item for
// players (and 5th for coaches) — the one approved exception to the
// 6-item budget, because the geomap is the surface a major equipment
// brand (Kookaburra) engaged with first. See docs/SPEC-performance-
// extension.md §4.
const PLAYER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/statistics", label: "My Stats", icon: BarChart3, tid: "nav-statistics" },
  { to: "/performance", label: "Log Match", icon: TrendingUp, tid: "nav-performance" },
  { to: "/development", label: "My Development", icon: Target, tid: "nav-development" },
  // Issue #184: "Coaching" pointed a player straight at /learn — a generic
  // technique-guide library — with nothing coach-authored on it, so a
  // player reading "Coaching" reasonably (and wrongly) expected their
  // coach's actual notes to live there. Relabelled per Martin's PR #192
  // answer 1 to describe what's actually behind this link; the coach's
  // real feedback stays where it already was (My Development -> Coach
  // Feedback tab), now surfaced instead via the Dashboard tile below.
  { to: "/learn", label: "Training & Guides", icon: BookOpen, tid: "nav-learn" },
  { to: "/grounds-map", label: "Grounds", icon: MapPin, tid: "nav-grounds" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
];

// Issue #222: "Feedback & Goals" and "Grounds" were dropped from this list.
// Both routes stay live and routable (App.js has no role guard removed) —
// this is a menu-only change:
// - `/coach-portal?tab=feedback` was the same page as "My Players"
//   (`/coach-portal`) with a different tab preselected — CoachPortal.jsx
//   defaults to `tab=goals` and renders Goals/Feedback as tabs on that one
//   screen, so the second sidebar entry was two destinations for one page.
//   The Dashboard's coach quick-nav tile still links there directly and
//   must keep working (verified — deep link, not a nav entry).
// - "Grounds" (`/grounds-map`) is the player-facing ground directory; a
//   coach reaches ground data through a linked player instead (#82). The
//   Dashboard coach tile linking to `/grounds-map` also stays.
// Issue #384 item 3 (Andreas's decision, 2026-08-20): "Dashboard" was the
// first entry here and is gone — Martin judged the coach dashboard thin
// enough to be noise until the coach side grows (#314, #383), so My Players
// is both the first entry and the page a coach signs in to
// (`lib/homePath.js`). Menu-only would NOT have been enough this time: the
// route is role-guarded in App.js too, because a nav entry is not the only
// way in (logo links, /career, a bookmark). `CoachDashboard` itself is
// untouched — restoring this is one line here and one word in App.js.
const COACH_NAV = [
  { to: "/coach-portal", label: "My Players", icon: Users, tid: "nav-coach-portal" },
  // Grounds is back (#1121; D221 O1, D223 Q3 — Andreas ruled that D221
  // supersedes #222's reason for dropping it, see the comment above). The
  // coach now has a Grounds destination in the new-look ribbon
  // (`kit/destinations.js` `COACH_DESTINATIONS`), and a coach on a shipped
  // page inside this Layout (`/grounds-map` itself, `/coach-portal/record`)
  // reaches it from the strip as well.
  { to: "/grounds-map", label: "Grounds", icon: MapPin, tid: "nav-grounds" },
  // Training (#101): #75 gave coaches the ability to SCHEDULE a session, but
  // that screen lives at /learn?tab=training and COACH_NAV had no entry
  // pointing there — so the capability was reachable only by typing the URL,
  // which to a user is indistinguishable from missing. `/learn` itself has no
  // role guard (App.js), so this is purely the missing way in.
  //
  // #80 re-homes training into a combined "Log Matches & Training" surface;
  // when it lands, this entry must be repointed rather than left dangling.
  { to: "/learn?tab=training", label: "Training", icon: BookOpen, tid: "nav-coach-training" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
];

// Issue #226 slice 1: admin used to be unreachable, so this nav was never
// actually exercised — it copied the full player set (minus Settings) plus
// a Coach Portal link. Three of those player links (`/statistics`,
// `/performance`, `/development`) assume the caller owns a player profile
// (they call e.g. `/stats/summary`/`/goals` with no `player_id`, which
// `resolveScope` requires explicitly for a non-player caller — 400/404 for
// admin) and are dropped here now that an admin can actually click them.
// `/learn` and `/grounds-map` are role-blind (verified) and stay; Coach
// Portal already works for admin today (`resolveScope`/`players.ts` give
// admin the full unfiltered roster, no coach_link needed).
// Issue #219 (epic #315): admin had NO nav entry into `/admin` at all until
// this — the guide review console was reachable only by typing the URL,
// which to a user is indistinguishable from not existing (same gap #101
// once left Training in for coaches). Placed ahead of Coach Portal: it is
// the admin-specific surface this persona is most likely to open first
// (the other four links are borrowed from the player nav, unchanged).
//
// Issue #1151 (D225 item 8, D233.7, Andreas 2026-10-02): admin stays
// laptop-first and the menu is trimmed to what account admin needs:
// Dashboard, Your players, Admin, Settings (G2: one menu, at most five).
// What left, and why:
//   - Training & Guides (`/learn`): reviewing guides is Admin > Guides;
//     reading them is not account admin.
//   - Grounds (`/grounds-map`): the players' directory. The grounds tools
//     an admin runs (add, bulk upload, merge duplicates, archive) live under
//     Admin > Venues and Admin > Grounds. An admin reaches the directory,
//     archived rows included, from the Venues section of `/admin`.
// Menu-only, as #222 did for coaches: both routes stay live by URL.
const ADMIN_NAV = [
  PLAYER_NAV.find((i) => i.to === "/dashboard"),
  { to: "/coach-portal", label: "Coach Portal", icon: Users, tid: "nav-coach-portal" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, tid: "nav-admin" },
  PLAYER_NAV.find((i) => i.to === "/settings"),
];

// Issue #804 fix round, F1 (Andreas ruled the menu label aligns "in the
// same slice" as the redrawn screen — about WHICH slice does it, not about
// bypassing the flag; the ruling's own stated reason, "so the screen
// carries one name, not three", only holds once the screen exists). Gated
// here rather than in COACH_NAV's own literal, so the shipped default
// (`newUi` off) stays "My Players" — the name every other unflagged
// surface still uses (`Dashboard.jsx`, `CoachDrillBankPanel.jsx`,
// `CoachPlayerRecord.jsx`, `LearningHub.jsx`) — and only flips once
// `coach-portal-empty` is itself on.
//
// D183 (Andreas, 2026-09-30, issue #950): D39 extends to admins — they
// read the same "Your players" wording as coaches (CoachDrillBankPanel.jsx
// and friends call `coachPlayersName()` for either role), so their own
// nav label follows the same flag rather than staying "Coach Portal"
// while the page they land on says "Your players". `ADMIN_NAV`'s literal
// stays "Coach Portal" so the flag-off leg is byte-identical to main; the
// admin branch below relabels the same way the coach branch already does.
function getNavForRole(role) {
  if (role === "coach") {
    return newUi("coach-portal-empty")
      ? COACH_NAV.map((i) => (i.to === "/coach-portal" ? { ...i, label: "Your players" } : i))
      : COACH_NAV;
  }
  if (role === "admin") {
    return newUi("coach-portal-empty")
      ? ADMIN_NAV.map((i) => (i.to === "/coach-portal" ? { ...i, label: "Your players" } : i))
      : ADMIN_NAV;
  }
  // Issue #762, ruling 9 / Decision 11 (2026-09-18): "My Stats" is absorbed
  // into the season record, so the nav entry disappears with the same flag
  // that gates the /statistics redirect (App.js) — same gating shape as
  // `coach-portal-empty` above, filtered out entirely rather than relabelled,
  // since there is nowhere left for this item to point once the destination
  // is gone from the player's own navigation.
  return newUi("season-record") ? PLAYER_NAV.filter((i) => i.to !== "/statistics") : PLAYER_NAV;
}

// Demo role switch (issue #37): the switcher used to live only on the
// unreachable Login page. Everything else (player, admin) flips to coach;
// coach flips back to player.
function otherDemoRole(role) {
  return role === "coach" ? "player" : "coach";
}

function roleLabel(role) {
  if (role === "coach") return "Coach";
  if (role === "admin") return "Admin";
  return "Player";
}

// NavLink's isActive only matches on pathname, so the two /coach-portal nav
// items (plain vs ?tab=feedback) both light up simultaneously. Resolve the
// intended tab from each side's search params and compare those instead.
function isCoachPortalItemActive(item, location) {
  if (location.pathname !== "/coach-portal") return false;
  const [itemPath, itemQuery] = item.to.split("?");
  if (itemPath !== "/coach-portal") return false;
  const itemTab = itemQuery ? new URLSearchParams(itemQuery).get("tab") : null;
  const currentTab = new URLSearchParams(location.search).get("tab");
  return (itemTab || "goals") === (currentTab || "goals");
}

function SidebarItem({ item }) {
  const location = useLocation();
  const isCoachPortalItem = item.to.startsWith("/coach-portal");
  return (
    <NavLink
      to={item.to}
      end={item.to === "/dashboard"}
      data-testid={item.tid}
      style={{ minHeight: 44 }}
      className={({ isActive }) => {
        const active = isCoachPortalItem ? isCoachPortalItemActive(item, location) : isActive;
        return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
          active
            ? "bg-white text-[#0B2E59] font-semibold shadow-sm"
            : "text-white/75 hover:bg-white/10 hover:text-white"
        }`;
      }}
    >
      <item.icon size={18} />
      <span>{item.label}</span>
    </NavLink>
  );
}

// The chromeless `<main>` that keeps the page padding (not a screen that owns its page) carries `data-nw-padded`, so the demo
// banner's gutter cap (index.css) skips it: the padding already is the gutter (#1498 review F1).
const paddedMainAttr = (ownsPageByScreen, chromeless) => (chromeless && !ownsPageByScreen ? { "data-nw-padded": "" } : {});

export default function Layout() {
  const { user, logout, exploreAs } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [switchingRole, setSwitchingRole] = useState(false);

  // Issue #189: the bell is its own component now (NotificationsBell.jsx),
  // and it owns everything it needs — the derived feed, the unread count,
  // the panel and its own `notifications_seen_at` watermark.
  //
  // What used to live here was #185's single-event-type bell: a
  // `/players/me` + `/notes` fetch pair, a `coach_feedback_seen_at` mirror,
  // and a `cc:coach-feedback-seen` window listener that let
  // DevelopmentPlan.jsx clear the badge when the player opened the feedback
  // tab. All of it is gone deliberately, not by accident: the bell no
  // longer keys off that watermark at all, and if it still listened, then
  // reading coach feedback would clear a badge that is also counting an
  // unread coach-assigned goal and an unread scheduled session. #184's
  // watermark still exists and still drives the Dashboard feedback tile —
  // it is simply no longer this component's business. The two surfaces are
  // allowed to disagree; that is the design (#189).
  const isPlayer = user?.role === "player";
  // #383: the coach's bell is no longer inert — see the topbar below.
  const isCoach = user?.role === "coach";

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  };

  // The sidebar's sign-out. It stays (#262 asks for sign-out to exist at
  // every viewport, not for this one to go), but it now goes through the
  // same `signOutAndGoHome` as the account menu and Settings — three
  // controls that end the same session must not end it three different
  // ways. It used to `nav("/login")` on its own.
  const doLogout = () => signOutAndGoHome(logout);

  // Demo role switch (issue #37): only path to the coach view is via the
  // Login page's "Explore as" buttons, which a fixture-authenticated user
  // never reaches (ProtectedRoute only redirects when `!user`). Surface the
  // switch directly in the always-visible demo banner instead.
  const switchDemoRole = async () => {
    setSwitchingRole(true);
    const nextRole = otherDemoRole(user?.role);
    const ok = await exploreAs(nextRole);
    setSwitchingRole(false);
    // The role being switched TO, not the one being switched from (#384
    // item 3): flipping to coach and landing on a dashboard the coach role
    // can no longer open would bounce, which reads as the switch misfiring.
    if (ok) nav(homePathForRole(nextRole));
  };

  const items = getNavForRole(user?.role);
  // Where the two logo links go (#384 item 3). A coach's home is My Players
  // now, so a mark labelled "go to your dashboard" would be both a wrong
  // label and a bounce.
  const home = homePathForRole(user?.role);
  const homeLabel = `CricketCompass — go to ${homeLabelForRole(user?.role)}`;

  // Issue #730/#754 — see CHROMELESS_ROUTES / isChromelessRoute above.
  // Narrowly scoped: only a named route, and only with ITS OWN flag on,
  // loses the sidebar and the mobile nav strip. The topbar (below) is
  // unconditional either way — `AccountMenu` there is the only sign-out
  // below `md`.
  // Issue #757 review round 2, B1 — `ownsPageByScreen` is the per-mount
  // sibling of `isChromelessRoute`: set by the RENDERED SCREEN itself
  // (`ChromelessOverrideContext`), for a route (`/performance`) where the
  // same path serves the shipped page AND, mid-flow, a fully redrawn one
  // with its own navy top bar — the route alone can't tell those apart.
  // Folded into `chromeless` so the sidebar/mobile-nav-strip suppression
  // (unchanged below) covers both mechanisms; the shared `<header>` is
  // suppressed ONLY by `ownsPageByScreen` (see that element's own
  // conditional) — `isChromelessRoute`'s two routes keep the shared header,
  // restyled, because THEIR drawings show it; `ownsPageByScreen` screens
  // draw their own.
  const [ownsPageByScreen, setOwnsPageByScreen] = useState(false);
  const chromeless = isChromelessRoute(location.pathname) || ownsPageByScreen;
  // Issue #797 fix round, B1 (review). The control is retired only where a
  // measured check (`frontend/e2e/text-size-tokens.spec.js`) actually
  // proves the screen's own marked content stays fixed px at Compact,
  // Default and Large — never on a guess. Two sources feed `hideTextSize`:
  //   - `hidesTextSizeControl(pathname)` (`lib/chromelessRoutes.js`) — for
  //     a route where route+flag IS the whole truth (one composition per
  //     flag state). Today that check FAILS on `/onboarding` and
  //     `/coach-setup` (both embed shared, still-rem components — see that
  //     file's own docstring and the filed issue), so this returns `false`
  //     for both until they are fixed or the shared component is.
  //   - `retiredByScreen` — set by the RENDERED SCREEN itself, via
  //     `RetireTextSizeContext`, for a route where the same path serves
  //     more than one composition (`/coach-portal`: the shipped, populated
  //     list vs. the redrawn empty state) and Layout has no way to tell
  //     them apart from the route alone.
  const [retiredByScreen, setRetiredByScreen] = useState(false);
  const hideTextSize = hidesTextSizeControl(location.pathname) || retiredByScreen;
  // Issue #780 (D140) — "the CURRENT screen's newUi flag is on" reuses this
  // SAME per-screen signal, not a second one. `retiredByScreen` is already
  // exactly "the rendered screen has told Layout it is a rebuilt screen"
  // (`RetireTextSizeContext`'s own docstring: built for `/coach-portal`,
  // the one shared-header route where route+flag alone can't tell the
  // shipped list from the redrawn empty state apart). It is NOT OR'd with
  // `hidesTextSizeControl(pathname)` here the way `hideTextSize` is: that
  // second source covers `/onboarding`/`/coach-setup`, whose OWN drawings
  // (`SetUp.dc.html`/`CoachSetUp.dc.html`) show the shared header WHITE,
  // not this NAVY bar — they get their own `whiteTopBar` branch below
  // instead (PR #887 review, B1), the same real-`TopBar` treatment on the
  // OTHER drawn surface. Only a screen that opts in through the context
  // gets the drawn NAVY anatomy from THIS branch — today that is
  // `CoachPortal.jsx` alone (D169.4, PR #920 review round 2): it is the
  // ONE owner of `setRetired` for `/coach-portal`, computed from
  // `lib/coachChromeGate.js`'s `computeDrawnCoachChrome` across BOTH
  // compositions that route can render (`CoachPortalEmptyState.jsx`, the
  // empty roster, and `CoachSquad.jsx`, a populated one) — neither child
  // component declares `retiredByScreen` itself any more (stale here since
  // #920; see `CoachPortal.jsx`'s own comment on `wantsDrawnCoachChrome`
  // for the two-writers bug that fixed).
  //
  // `!ownsPageByScreen` matters here, not just in the JSX gate below: EVERY
  // screen that supplies its own complete page (`RecordEmptyScreen.jsx`,
  // `RecordOneMatchScreen.jsx`, `LogGroundFlow.jsx`, `LogScorecardFlow.jsx`,
  // `LogSavedFlow.jsx`) ALSO calls `useRetireTextSize().setRetired(true)` —
  // they need `hideTextSize` true too, for their OWN `AccessibilityMenu`.
  // Found live (journey 35, strict-mode violation: two `topbar-notifications`
  // elements): without this guard, `drawnTopBar` came out true for those
  // screens as well, and Layout rendered ITS OWN navy `<TopBar>` (below)
  // ALONGSIDE the screen's own — the screen already draws a complete page,
  // so Layout must never add a second bar on top of it. `retiredByScreen`
  // WITHOUT an owned page is exactly, and only, `/coach-portal` today —
  // `CoachPortal.jsx` calls `useRetireTextSize()` alone (D169.4), never
  // `useChromelessOverride()` — see that file's own "missing TopBar" gap
  // this branch closes.
  const drawnTopBar = retiredByScreen && !ownsPageByScreen;
  // #1471 (D395, Q35; D385) — the coach's shell is the player's: the ribbon only, no sidebar, from 768 up. The drawn
  // coach chrome (`drawnTopBar` for a coach: the white bar and the coach ribbon, `DrawnTopBar`) is the new-look coach
  // portal, so that is where the sidebar goes. The old look never reaches `drawnTopBar`, and an admin never does
  // (`computeDrawnCoachChrome` is coach-only), so the shipped sidebar, and the admin's (D76), are untouched. What the
  // sidebar carried lives on: Your players, Grounds and Training & guides in the coach ribbon; Settings and Sign out in
  // the account menu; the mark in the top bar is the way home.
  const coachRibbonShell = drawnTopBar && isCoach;
  // Everything that used to key off "no sidebar" keys off this: a chromeless route and the coach ribbon shell.
  const noSidebar = chromeless || coachRibbonShell;
  // Issue #1261 — the main column's margin animates ONLY when the sidebar
  // collapses or expands (it was there before this render and is there now).
  // When the sidebar appears or disappears — a screen taking the page over,
  // or giving it back — the margin snaps. The old unconditional
  // `transition-[margin]` slid the whole page 256px sideways for 200ms each
  // time the record screen mounted at desktop width, under a sidebar that
  // was already gone; a phone-width measurement taken mid-slide read the
  // column up to 32px past the viewport (journey 41).
  const hadSidebar = useRef(!noSidebar);
  const animateMargin = !noSidebar && hadSidebar.current;
  useEffect(() => {
    hadSidebar.current = !noSidebar;
  }, [noSidebar]);
  // PR #887 review, B1 — the WHITE-surface sibling of `drawnTopBar`.
  // `isChromelessRoute(pathname)` is already exactly "this route's own
  // `newUi(...)` flag is on and it is one of the two set-up routes"
  // (`lib/chromelessRoutes.js`) — the same route+flag fact `chromeless`
  // already folds in. `SetUp.dc.html`/`CoachSetUp.dc.html` both draw the
  // shared header WHITE, restyled (a navy/gold `TopBarMark`, borderless
  // #64748B glyphs, an empty account circle), not the shipped
  // `CompassLogo` lockup and bordered "Aa"/Contrast ring the hand-rolled
  // `<header>` below still drew for these two routes before this review
  // round. `!ownsPageByScreen` is defensive, not load-bearing today —
  // neither set-up route ever sets it — kept for the same reason
  // `drawnTopBar` carries it: a screen that supplies its own complete page
  // must never also get a bar from here.
  const whiteTopBar = isChromelessRoute(location.pathname) && !ownsPageByScreen;
  // #1428 (D385, D387) — the page container. A route is "new look" exactly when Layout has dropped the
  // shipped shell for it: `chromeless` (a screen that owns its page, or a chromeless set-up route), the same
  // state that removes the sidebar and the mobile strip. Only then does the outer wrapper carry `nw-shell`
  // (index.css caps and centres the content and aligns the bands to the wide column, from md up and only then).
  // The old look never gets the class, so nothing changes for it at any width; admin keeps its sidebar on the
  // shipped layout (D76) and is never chromeless. The drawn coach portal (`coachRibbonShell`, #1471, D395) has no
  // sidebar either now, so it is wrapped like the player's shell. `pageWidth` is the column the rendered screen asked for (`usePageWidth`): narrow (640px)
  // unless it says wide (1040px).
  const newLook = chromeless || coachRibbonShell;
  const [pageWidth, setPageWidth] = useState("narrow");

  return (
    <div className={`min-h-screen flex bg-slate-50${newLook ? " nw-shell" : ""}`} data-nw-width={newLook ? pageWidth : undefined}>
      {/* Sidebar — absent on a chromeless route (#730/#754): nothing to
          navigate to yet. */}
      {!noSidebar && (
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`hidden md:flex fixed z-30 h-screen ${collapsed ? "w-[72px]" : "w-64"} flex-col text-white transition-[width] duration-200`}
        style={{ background: "#0B2E59" }}
      >
        <div className={`${collapsed ? "px-2 py-4" : "px-4 py-5"} border-b border-white/10`}>
          {/* Issue #263: a link home, like the topbar mark and like the
              landing/register pages (#228). It was a bare div, so the one
              navigation convention users apply without being taught did
              nothing on the surface they use every day. */}
          {/* min-w-0, NOT shrink-0. #263 used shrink-0 here and it was wrong:
              the inline CompassLogo is a lockup (mark + wordmark + eyebrow)
              built to compress — it carries min-w-0/max-w-full/truncate
              internally — and shrink-0 forbade exactly that. At phone-390 the
              row then could not compress and the overflow surfaced two
              elements further right, reported as `topbar-user` sitting 2-3px
              past the viewport (#273). min-w-0 lets this flex item shrink
              below its content width, which is what makes the lockup's own
              truncation reachable. */}
          <Link
            to={home}
            aria-label={homeLabel}
            className={`bg-white rounded-2xl ${collapsed ? "p-2" : "px-4 py-5"} w-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
          >
            {collapsed ? <CompassLogo size={40} /> : <CompassLogo size={64} stacked />}
          </Link>
          {!collapsed && (
            <p className="text-[11px] leading-snug text-white/70 mt-4 px-2 text-center">
              We don't just track performance. We guide progress.
            </p>
          )}
        </div>

        {/* Collapse toggle — sits just below the logo, above the nav */}
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`mx-3 mt-3 flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-all`}
        >
          {!collapsed && <span className="text-[11px] uppercase tracking-widest font-semibold">Collapse</span>}
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto thin-scroll`}>
          {items.map((item) => (
            <SidebarItem key={item.to} item={item} />
          ))}
        </nav>
        <div className={`${collapsed ? "p-2" : "p-3"} border-t border-white/10`}>
          <button
            data-testid="logout-btn"
            data-leave="true"
            onClick={doLogout}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm text-white/75 hover:bg-white/10 hover:text-white transition-all`}
          >
            <LogOut size={18} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>
      )}

      {/* Main */}
      {/* min-w-0 (issue #37 mobile QA): without it this flex item's intrinsic
          content width (topbar row) pushes the whole page wider than the
          viewport on mobile, clipping the banner and everything below it.
          No sidebar margin on a chromeless route — there is no sidebar to
          clear (#730/#754). */}
      <div className={`flex-1 min-w-0 ${noSidebar ? "" : collapsed ? "md:ml-[72px]" : "md:ml-64"} flex flex-col${animateMargin ? " transition-[margin] duration-200" : ""}`}>
        {/* Topbar. Issue #792 — `px-6 py-3` (Tailwind, `rem`) is the
            shipped padding, unconditional now (PR #887 review, B1
            retired the `chromeless`-restyled branch this comment used to
            describe): `isChromelessRoute`'s two routes (`/onboarding`,
            `/coach-setup`) no longer reach this element AT ALL with their
            own flag on — `whiteTopBar` below renders the kit's real white
            `TopBar` instead, so this `<header>` is reached only with
            `chromeless` false, i.e. it is still exactly the topbar for
            every old, unflagged screen, #792's own scope. */}
        {/* Issue #757 review round 2, B1 — `ownsPageByScreen` suppresses
            this shared, always-white header entirely. `DirectionA1`/
            `FirstGround` (the #757 step) draw a NAVY bar with no bell, no
            accessibility trigger and no account avatar — a different
            composition this shared header cannot become by restyling
            alone, so the screen supplies its own
            (`components/kit/TopBar variant="navy"`, `LogGroundFlow.jsx`)
            and this element steps aside for it. */}
        {/* Issue #780 (D140) — `drawnTopBar` (`retiredByScreen`, see that
            variable's own comment) is the THIRD shape a rebuilt screen can
            ask this shared slot for, next to `chromeless`'s restyled-white
            and `ownsPageByScreen`'s own-complete-page: keep the shared
            header, but render it as the kit's real navy `TopBar` — the
            same "the app's REAL topbar" component `OnboardingNew.jsx`'s
            own docstring names, reused wholesale rather than a fourth
            hand-rolled copy of its markup. `/coach-portal` is the one
            route that asks for this today — both its redrawn empty state
            (`CoachPortalEmptyState.jsx`) and its redrawn, populated squad
            (`CoachSquad.jsx`, #920/D169.4) can trigger it, via
            `CoachPortal.jsx`'s own `computeDrawnCoachChrome`, not just the
            empty one: `CoachPortal.jsx`'s own comment names "the
            missing TopBar" as a known gap #803 deliberately left open
            (that issue is about whether the page should own itself
            instead — undecided) — this branch answers the narrower,
            decided question D140 actually rules on: what the bar looks
            like wherever it renders, not which mechanism seats it. Sidebar
            visibility is UNCHANGED by this branch (still keyed to
            `chromeless` alone, unaffected by `drawnTopBar`) —
            `/coach-portal` is not a chromeless route, so the sidebar still
            renders exactly as it does today; this is the bar restyling,
            not a page replacement. The mobile nav strip is NOT unchanged
            any more (audit G7/#870, D173.C1) — see that element's own
            comment, further down, for why it now also keys off
            `drawnTopBar`. */}
        {drawnTopBar && (
        <DrawnTopBar
          isCoach={isCoach}
          isPlayer={isPlayer}
          pathname={location.pathname}
          homeHref={home}
          homeLabel={homeLabel}
          hideTextSize={hideTextSize}
          onPlayersClick={
            // F3: on a player's own page "Your players" is the list's way
            // back; on the list itself it stays a plain current marker.
            new URLSearchParams(location.search).get("player") ? () => nav("/coach-portal") : undefined
          }
        />
        )}
        {/* PR #887 review, B1 — `whiteTopBar`'s own kit TopBar, replacing
            the hand-rolled white `<header>` below for exactly the two
            routes `SetUp.dc.html`/`CoachSetUp.dc.html` draw: their own
            flag on, per `isChromelessRoute`. No `rightLabel` — neither
            drawing puts a caption in this bar (unlike `CoachPortalEmpty
            .dc.html`'s "Coach portal"). `surface="white"` on the bell
            only changes its unread badge's colours (D159); the glyph
            itself is already `#64748B` by `AccessibilityMenu`'s/
            `NotificationsBell`'s own default.
            Issue #892 (D171 X2) — `surface="white"` now also joins
            `AccessibilityMenu`/`AccountMenu`, so their own focus-visible
            outline reads navy against this white bar instead of the
            navy-bar default (white). */}
        {whiteTopBar && (
        <TopBar
          variant="white"
          homeHref={home}
          homeLabel={homeLabel}
          endControls={
            <>
              {(isPlayer || isCoach) && <NotificationsBell compact surface="white" />}
              <AccessibilityMenu hideTextSize={hideTextSize} surface="white" />
              <AccountMenu empty surface="white" />
            </>
          }
          data-testid="layout-topbar-white"
        />
        )}
        {!ownsPageByScreen && !drawnTopBar && !whiteTopBar && (
        <header data-testid="layout-topbar-legacy" className={`sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-4 ${chromeless ? "" : "px-6 py-3"}`} style={chromeless ? { padding: "var(--nw-space-topbar-y) var(--nw-space-page)" } : undefined}>
          {/* Issue #263. Two things, and the link is the more important one.

              Position: this is the FIRST child so the logo is leftmost at
              every viewport. It used to be last — which on desktop only made
              it a duplicate of the sidebar mark, but the sidebar is
              `hidden md:flex`, so on a phone the app's only logo sat in the
              top-RIGHT corner.

              Link: it is now a link home, which it has never been anywhere in
              the app shell. #228 wraps the logo in a Link on the landing and
              register pages because a stranger expects that; the signed-in
              shell was the one place the convention broke. The signed-in
              home and not `/` — `/` redirects there for an authenticated
              visitor, so linking the real destination avoids a pointless
              bounce. Since #384 item 3 that home is role-dependent: the
              dashboard for a player or admin, My Players for a coach. */}
          <Link
            to={home}
            aria-label={homeLabel}
            className="min-w-0 inline-flex items-center rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            style={{ minHeight: "var(--nw-size-tap)" }}
            data-testid="topbar-logo"
          >
            {/* #785 (moved from #780) — the small mark and the single word,
                no tagline. `size={72}` rendered the full two-tone
                "CricketCompass" lockup plus the "NAVIGATE. DEVELOP.
                PERFORM." eyebrow, both `truncate`d — which clipped
                mid-word ("CricketC…" / "NAVIGATE. DEVEL…") in this exact
                row at phone-390, confirmed by screenshot on the merged
                set-up screen (#780's review). `compact` (CompassLogo.jsx)
                is the anatomy SetUp.dc.html draws: a 22px mark next to the
                single word "Compass", nothing else. See CompassLogo.jsx's
                own comment for why this is a distinct branch rather than a
                smaller `size` on the two-tone wordmark path — every other
                call site (sidebar, login, register, reset-password) is
                unchanged. Not gated to a breakpoint: the desktop-specific
                composition question #780 deferred has not been through its
                own design pass yet, so this ships everywhere until it has,
                rather than adding an undesigned split. The accessibility
                control (AccessibilityMenu, below) is untouched — it is a
                real feature the drawings simply did not draw. */}
            <CompassLogo size={22} compact />
          </Link>
          {/* The strapline only. The wordmark that used to sit here rendered
              "CricketCompass" a second time in the same row as CompassLogo's
              own — duplication that was invisible while the two were at
              opposite ends of the header and obvious once they are adjacent.
              `ml-auto` on the NEXT element does the spacing now. */}
          <div className="hidden xl:block text-[11px] text-slate-500 leading-tight border-l border-slate-200 pl-4 min-w-0">
            We don't just track performance. We guide progress.
          </div>
          {/* Flex spacer: the logo anchors left, everything else sits right.
              `ml-auto` lives here rather than on the bell because the bell
              does not render for every role (#189/#383) and a spacer keeps
              the alignment of the rest of the row independent of that. */}
          <div className="ml-auto" aria-hidden="true" />
          {/* Issues #189 / #383 — player OR coach, and still ABSENT rather
              than inert for anyone else.

              #185 shipped the coach a decorative bell; #189 removed it,
              because every event the feed could express back then was
              authored BY a coach and a permanent 0 is the same dead-bell
              defect one layer up. #383 supplies the events that were
              missing — a linked player achieving a goal, posting a match or
              season reflection, or completing Identity & Vision — so a
              coach's bell now has something to say and comes back.

              ADMIN IS STILL EXCLUDED, deliberately: `GET /api/notifications`
              404s an admin (they hold neither a player profile nor coach
              links), and a bell whose only outcome is an error state is
              worse than no bell. The topbar is otherwise role-blind (only
              the sidebar nav is role-keyed), so this stays a deliberate
              role check rather than an inherited one. */}
          {(isPlayer || isCoach) && <NotificationsBell />}
          <AccessibilityMenu hideTextSize={hideTextSize} />
          {/* Issue #318. This used to be an inert div — a name and an avatar
              with nothing behind them. It is now the account menu (Profile ·
              Settings · Sign out), which is also the only sign-out control
              that exists below `md` (#262): the sidebar's is inside a
              `hidden md:flex` aside. AccountMenu.jsx keeps `topbar-user` on
              its trigger, so the overflow attribution (#273/#276) still
              names the same box. */}
          <AccountMenu surface="white" />
        </header>
        )}

        {/* Mobile nav strip — absent on a chromeless route (#730/#754):
            nothing to navigate to yet.

            Audit G7 (#870), D173.C1 — also absent wherever the drawn
            coach bar shows (`drawnTopBar`, above): D92 draws "no
            destination row" for a coach, and `CoachPortalEmpty.dc.html`
            draws no strip under its own navy bar either. `drawnTopBar` is
            `retiredByScreen && !ownsPageByScreen` — the SAME signal
            `CoachPortal.jsx` sets from `computeDrawnCoachChrome`
            (`lib/coachChromeGate.js`) for the bar itself, read here rather
            than duplicated, so CoachPortal stays the one owner of this
            route's chrome (D169.4) and Layout gains no second writer.

            RESOLVED (#962, D177): with the strip gone, a coach at 390 used
            to reach Settings through the account menu (`AccountMenu`,
            still rendered on the navy `TopBar` above) but have no way to
            Training — the strip's own `nav-coach-training` entry (label
            "Training") was the only path there below `md`, and D92's drawn
            destination row (`--nw-page-stack`, Record · Plan · Grounds) is
            a player-only row with no coach items at all. `AccountMenu` now
            carries a coach-only Training item of its own (same
            destination, `/learn?tab=training` — see that component's own
            docstring) for exactly this reason, so the gap named here is
            closed rather than hypothetical-and-open. */}
        {!chromeless && !drawnTopBar && (
        <div className="md:hidden nw-mobile-strip bg-[#0B2E59] text-white px-2 py-2 overflow-x-auto flex gap-2">
          {items.map((item) => {
            const isCoachPortalItem = item.to.startsWith("/coach-portal");
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                className={({ isActive }) => {
                  const active = isCoachPortalItem ? isCoachPortalItemActive(item, location) : isActive;
                  return `whitespace-nowrap px-3 py-1.5 rounded-md text-xs ${
                    active ? "bg-white text-[#0B2E59] font-semibold" : "text-white/80"
                  }`;
                }}
              >
                {item.label}
              </NavLink>
            );
          })}
        </div>
        )}

        {/* PR #826 review, B1 — `p-6 md:p-8` (Tailwind, `rem`) is the shipped
            page padding and stays exactly that on every non-chromeless
            route, same reasoning as the `<header>` fix just above: this
            `<main>` is unconditional, so it still wraps every old screen
            too. On a chromeless route the same `--nw-space-page` token
            already gives the header its horizontal half; SetUp.dc.html:64,
            CoachSetUp.dc.html:59 and CoachPortalEmpty.dc.html:56 each draw
            their own page body with `padding: 20px` on the very same line
            that draws the `gap` this PR already fixed — the review found
            the padding half of that same line untouched, rendering
            22.5/25.5/28.5 at Compact/Default/Large against the drawn 20.
            `chromeless` is true only when the reached screen's own
            `newUi(...)` flag is on (see the `<header>` comment), so this
            never touches a shipped, flag-off screen. CoachPortalEmpty is
            NOT chromeless (`/coach-portal` isn't gated in
            `chromelessRoutes.js`) and stays on the shipped padding here —
            that screen's own page body/top-bar model is #803's, not this
            issue's; CoachPortalEmpty.dc.html:48 draws ITS top bar at
            `padding: 14px 20px`, not this fix's 12/20, a fact worth
            carrying into that issue rather than guessing here. */}
        {/* Issue #757 review round 2, B1 — `ownsPageByScreen` gets NO
            padding here at all, same reason it gets no shared header: the
            screen supplies its own navy `TopBar` edge to edge and its own
            content padding below it (`DirectionA1.dc.html`'s drawn
            `padding: 24px 20px` on the content block, not this shared
            `<main>`'s token) — giving it ambient padding too would double
            it and leave the top bar unable to reach the viewport edge. */}
        {/* #1487 (D395): the coach shell (ribbon only, no sidebar) reads the player shell's gutter from md. Its `<main>` keeps
            `p-6` on a phone, byte for byte, and from md drops the side padding (`md:px-0`; the top and bottom stay `py-8`); the
            blocks that carry no gutter of their own take `--nw-space-page` instead (index.css, `data-coach-shell`). */}
        <main
          className={`flex-1 ${ownsPageByScreen ? "" : chromeless ? "" : coachRibbonShell ? "p-6 md:px-0 md:py-8" : "p-6 md:p-8"}`}
          data-coach-shell={coachRibbonShell && !ownsPageByScreen && !chromeless ? "" : undefined}
          {...paddedMainAttr(ownsPageByScreen, chromeless)}
          style={ownsPageByScreen ? undefined : chromeless ? { padding: "var(--nw-space-page)" } : undefined}
        >
          {/* Rendered ONLY for a demo persona (issue #557): `is_demo` is the
              server's verdict (publicUser, api/src/routes/auth.ts), computed
              against the seed's own demo-account constants — the client
              never matches on email strings. Until #557 this banner rendered
              unconditionally, which was invisible while production WAS the
              demo; once real accounts existed, its switch button replaced a
              real user's session with a demo account's. A real account gets
              nothing here — no banner, no switch, nothing in the DOM.

              For demo personas it stays non-dismissible (issue #37): this
              banner is the only discoverable path to the role switch, so it
              must stay visible. The original reason given here — "the topbar
              avatar has no menu to fall back to" — stopped being true with
              #318, but the conclusion did not: the account menu is about
              YOUR account (Profile / Settings / Sign out) and deliberately
              carries no demo-persona switch, which is a property of the demo
              environment rather than of the signed-in user. */}
          {user?.is_demo && (
            <div
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-2.5 mb-6 text-sm"
              data-testid="demo-banner"
            >
              <span className="flex items-start gap-2 min-w-0">
                <Info size={15} className="shrink-0 mt-0.5" />
                <span>
                  Demo environment — data resets on refresh. Exploring as{" "}
                  <strong className="font-semibold">{roleLabel(user?.role)}</strong>
                  {user?.display_name ? ` — ${user.display_name}` : ""}.
                </span>
              </span>
              <button
                type="button"
                onClick={switchDemoRole}
                disabled={switchingRole}
                data-testid="demo-banner-switch-role"
                data-leave="true"
                className="shrink-0 self-start sm:self-auto min-h-[44px] whitespace-nowrap rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 transition-colors disabled:opacity-60"
              >
                {switchingRole ? "Switching…" : `Switch to ${roleLabel(otherDemoRole(user?.role))}`}
              </button>
            </div>
          )}
          {/* Issue #797 fix round, B1 — the one hook a rendered screen has
              to tell Layout "retire the text-size control while I'm on
              screen", for a route (`/coach-portal`) where the route alone
              doesn't say which composition is showing. See
              `context/RetireTextSizeContext.js`. */}
          <RetireTextSizeProvider value={{ retired: retiredByScreen, setRetired: setRetiredByScreen }}>
            {/* Issue #757 review round 2, B1 — the sibling handle, one
                layer up: "I supply my own complete page." See
                `context/ChromelessOverrideContext.js`. */}
            <ChromelessOverrideProvider value={{ active: ownsPageByScreen, setActive: setOwnsPageByScreen }}>
              <PageWidthProvider value={{ setWidth: setPageWidth }}>
                <Outlet />
              </PageWidthProvider>
            </ChromelessOverrideProvider>
          </RetireTextSizeProvider>
        </main>
      </div>
    </div>
  );
}
